import { InsightDirection, InsightFilter } from '@api/api/insights';
import {
  KgInsightCategory,
  KgInsightSentiment,
  KgInsightSummaryV2,
} from '@api/api/kg-insights';

import {
  PulseCompanySentiment,
  PulseInsightCategory,
  PulseInsightDriver,
  PulseSentimentDirection,
  PulseSentimentInsight,
} from '../pulse-types';

const toDriverSentiment = (
  sentiment: KgInsightSentiment | undefined
): PulseCompanySentiment | undefined => {
  if (sentiment === 'positive') {
    return 'bullish';
  }

  if (sentiment === 'negative') {
    return 'bearish';
  }

  return undefined;
};

/*
 * "Driven by" on a theme page is the companies in the insight: the summary's
 * mentions[] rows with ref_type=entity and source subject|cohort_asset (see
 * the pulse solution design, POST /kg-insights/v2/filter). Names and tickers
 * are resolved client-side by PulseCompanyCell via useEntity.
 *
 * Per the Figma annotations the list is filtered to the drawer's direction
 * (tailwind -> bullish companies only, headwind -> bearish only). The "all"
 * drawer is unfiltered, so each company keeps its own mention sentiment.
 */
const toDrivenBy = (
  mentions: KgInsightSummaryV2['mentions'],
  sentiment: PulseCompanySentiment | undefined
): PulseInsightDriver[] => {
  const drivers = new Map<string, PulseInsightDriver>();

  for (const mention of mentions ?? []) {
    const driverSentiment = toDriverSentiment(mention.sentiment ?? undefined);

    if (
      mention.ref_type === 'entity' &&
      (mention.source === 'subject' || mention.source === 'cohort_asset') &&
      driverSentiment !== undefined &&
      (sentiment === undefined || driverSentiment === sentiment) &&
      !drivers.has(mention.ref_id)
    ) {
      drivers.set(mention.ref_id, {
        entityTag: mention.ref_id,
        name: '',
        ticker: '',
        sentiment: driverSentiment,
      });
    }
  }

  return [...drivers.values()];
};

const DIRECTION_TO_SENTIMENT: Record<
  PulseSentimentDirection,
  PulseCompanySentiment | undefined
> = {
  tailwind: 'bullish',
  headwind: 'bearish',
  all: undefined,
};

const KG_TO_PULSE_CATEGORY: Record<KgInsightCategory, PulseInsightCategory> = {
  company_catalyst: 'company_catalyst',
  market_catalyst: 'market_catalyst',
  earnings_recap: 'earnings_recap',
  earnings_preview: 'earnings_recap',
};

export const toPulseInsight = (
  summary: KgInsightSummaryV2,
  direction: PulseSentimentDirection
): PulseSentimentInsight => {
  const sentiment = DIRECTION_TO_SENTIMENT[direction];
  const category = KG_TO_PULSE_CATEGORY[summary.category];
  const company =
    summary.subject.type === 'entity'
      ? { entityTag: summary.subject.id, name: '', ticker: '' }
      : undefined;
  const earnings =
    (summary.category === 'earnings_preview' ||
      summary.category === 'earnings_recap') &&
    summary.subject.ref?.type === 'earnings'
      ? {
          id: summary.subject.ref.id,
          category: summary.category,
        }
      : undefined;

  // Per the Figma annotations, company-scoped insights (company catalyst /
  // earnings recap) are always driven by the catalyst owner only.
  const isCompanyScoped =
    category === 'company_catalyst' || category === 'earnings_recap';
  const companySentiment = sentiment ?? toDriverSentiment(summary.sentiment);
  const companyScopedDrivenBy =
    company && companySentiment
      ? [{ ...company, sentiment: companySentiment }]
      : [];

  return {
    id: summary.id,
    category,
    subcategory: summary.sub_category,
    subjectThemeId:
      summary.subject.type === 'theme' ? summary.subject.id : undefined,
    date: summary.created_at,
    title: summary.title,
    summary: summary.subtitle,
    company,
    earnings,
    // TODO(KG-733): the 1D reaction metric is not part of the /v2/filter
    // contract; the design sources it from entity-performance client-side.
    drivenBy: isCompanyScoped
      ? companyScopedDrivenBy
      : toDrivenBy(summary.mentions, sentiment),
  };
};

export const toPulseScenarioInsight = (
  insight: InsightFilter,
  reactionPercent?: number
): PulseSentimentInsight => {
  const company = {
    entityTag: insight.metadata.entity,
    name: '',
    ticker: '',
  };

  return {
    id: insight.id,
    category: 'scenario_insight',
    date: insight.created_at,
    title: insight.simple_title ?? insight.card.name,
    summary: insight.card.intuition,
    company,
    reactionPercent,
    scenario: {
      episodeReturnPercent: insight.metadata.median_return,
      horizon: insight.metadata.horizon,
      starRating: insight.metadata.stars,
      episodeCount: insight.metadata.episode_count,
    },
    drivenBy: [
      {
        ...company,
        sentiment:
          insight.metadata.direction === InsightDirection.Bullish
            ? 'bullish'
            : 'bearish',
      },
    ],
  };
};
